import type { WebGLShaderProgram } from './shader';

export class TexturePool {
  private readonly gl: WebGL2RenderingContext;
  private readonly pool: WebGLTexture[] = [];
  private readonly inUse = new Set<WebGLTexture>();

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  acquire(width: number, height: number): WebGLTexture {
    const gl = this.gl;
    let tex = this.pool.pop();
    if (!tex) {
      tex = gl.createTexture();
      if (!tex) throw new Error('Failed to create WebGL texture');
    }
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.inUse.add(tex);
    return tex;
  }

  release(tex: WebGLTexture): void {
    if (!this.inUse.has(tex)) return;
    this.inUse.delete(tex);
    this.pool.push(tex);
  }

  dispose(): void {
    const all = this.pool.concat(Array.from(this.inUse));
    for (let i = 0; i < all.length; i += 1) {
      this.gl.deleteTexture(all[i]);
    }
    this.pool.length = 0;
    this.inUse.clear();
  }
}

export function uploadCanvasToTexture(
  gl: WebGL2RenderingContext,
  tex: WebGLTexture,
  source: HTMLCanvasElement | OffscreenCanvas | ImageData,
): void {
  gl.bindTexture(gl.TEXTURE_2D, tex);
  if (source instanceof ImageData) {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, source.width, source.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, source.data);
  } else {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source as TexImageSource);
  }
}

export function readTextureToCanvas(
  gl: WebGL2RenderingContext,
  tex: WebGLTexture,
  width: number,
  height: number,
  program: WebGLShaderProgram,
  pool: TexturePool,
): HTMLCanvasElement {
  const fb = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);

  const pixels = new Uint8Array(width * height * 4);
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.deleteFramebuffer(fb);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const imageData = ctx.createImageData(width, height);
    // WebGL origin is bottom-left; flip for canvas
    for (let y = 0; y < height; y += 1) {
      const srcRow = (height - 1 - y) * width * 4;
      imageData.data.set(pixels.subarray(srcRow, srcRow + width * 4), y * width * 4);
    }
    ctx.putImageData(imageData, 0, 0);
  }
  return canvas;
}

export function blitTexture(
  gl: WebGL2RenderingContext,
  program: WebGLShaderProgram,
  source: WebGLTexture,
  target: WebGLTexture | null,
  width: number,
  height: number,
): void {
  const fb = target ? gl.createFramebuffer() : null;
  if (fb && target) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target, 0);
  } else {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
  gl.viewport(0, 0, width, height);
  program.use();
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, source);
  program.setUniform1i('u_texture', 0);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  if (fb) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);
  }
}
